/// <amd-module name="Wit/WorkItems/Redux/Sagas/WorkItemSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { RT } from "redux/Utils/SagaHelper";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { traceException } from "VSS/Platform/Trace";
import { WorkItemLinkedItem } from "Wit/Clients/Internal/Contracts/SocialWorkItem";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/CoreFieldRefNames";
import { getFieldFlags } from "Wit/WorkItems/DataSources/FieldFlagsDataSource";
import * as Resources from "Wit/WorkItems/Resources";
import { getNewTemporaryWorkItemId, transformWorkItemFields } from "Wit/WorkItems/Utilities/WorkItemUtilities";
import { FieldValueType, IClassificationFieldState } from "Wit/WorkItemTypes/Contracts";
import { ClassificationNodeActions } from "Wit/WorkItemTypes/Module/ClassificationNodeActions";
import { getAreaPathState, getClassificationNodesByType, getIterationPathState } from "Wit/WorkItemTypes/Selectors/ClassificationNodeSelectors";
import { getWorkItemTypeFields } from "Wit/WorkItemTypes/Selectors/WorkItemTypeSelectors";
import { createWorkItemAsync, deleteWorkItemAsync, getWorkItemsAsync, restoreWorkItemAsync } from "../../DataSources/WorkItemDataSource";
import * as WorkItemFieldsAllowedValuesDataSource from "../../DataSources/WorkItemFieldsAllowedValuesDataSource";
import { Area, Feature } from "../../TelemetryConstants";
import { splitWorkItemRelations } from "../../Utilities/WorkItemRelationUtilities";
import { getFieldsWithValues, isWorkItemNew } from "../../Utilities/WorkItemUtilities";
import { WorkItemActions, WorkItemActionTypes } from "../../WorkItemActions";
import { IWorkItem, WorkItemStatus } from "../../WorkItemContracts";
import { canCreateWorkItem, getAllWorkItemIds, getWorkItem, getWorkItemProjectId, getWorkItemTypeName } from "../../WorkItemSelectors";
import { workItemChangeProcessor } from "./WorkItemChangeSaga";

/**
 * Root work item saga.
 */
export function* workItemSaga(): SagaIterator {
    yield effects.takeEvery(WorkItemActionTypes.WorkItemsLoadRequested, loadWorkItems);

    yield effects.takeEvery(WorkItemActionTypes.WorkItemCreateRequested, createWorkItem);

    yield effects.takeEvery(WorkItemActionTypes.WorkItemDeleteRequested, deleteWorkItem);

    yield effects.takeEvery(WorkItemActionTypes.LoadAllowedValues, loadAllowedValues);

    yield effects.takeEvery(WorkItemActionTypes.LoadFieldFlags, loadFieldFlags);

    yield effects.takeEvery(WorkItemActionTypes.WorkItemChangeRequested, workItemChangeProcessor);

    yield effects.takeEvery(WorkItemActionTypes.InitializeNewWorkItem, initializeNewWorkItem);

    yield effects.takeEvery(WorkItemActionTypes.WorkItemRestoreRequested, restoreWorkItem);
}

function* loadWorkItems(action: ActionsOfType<WorkItemActions, WorkItemActionTypes.WorkItemsLoadRequested>): SagaIterator {
    const { workItemsRequestData, fields, useCached } = action.payload;
    const workItemIds = workItemsRequestData.map(data => data.workItemId);
    const workItemIdToProjectMap: { [workItemId: number]: string } = {};
    workItemsRequestData.forEach(data => {
        workItemIdToProjectMap[data.workItemId] = data.projectId;
    });

    let workItemIdsToLoad = workItemIds;

    if (useCached) {
        const cachedWorkItemIds: RT<typeof getAllWorkItemIds> = (yield effects.select(getAllWorkItemIds)) as any;
        // retrieve work items which are not loaded yet
        const cachedWorkItemIdsSet = new Set(cachedWorkItemIds || []);
        workItemIdsToLoad = workItemIds.filter(id => !cachedWorkItemIdsSet.has(id));
    }

    let failedWorkItemIds: number[] = [];
    let loadedWorkItems: IWorkItem[] = [];

    if (workItemIdsToLoad.length > 0) {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

        // mark the work items as loading in state
        yield effects.put(
            WorkItemActions.updateWorkItemsStatus(
                workItemIdsToLoad.map(id => ({
                    workItemId: id,
                    status: WorkItemStatus.Loading
                }))
            )
        );

        // load the workitems. If a work item for a id doesnt exist, it will come as null in the workItems array
        const workItems: RT<typeof getWorkItemsAsync> = (yield effects.call(getWorkItemsAsync, pageContext, workItemIdsToLoad, fields)) as any;
        loadedWorkItems = workItems
            .filter(w => w != null)
            .map(w => ({
                id: w.id,
                fields: w.fields,
                projectId: workItemIdToProjectMap[w.id],
                workItemTypeName: w.fields[CoreFieldRefNames.WorkItemType]
            }));

        // get work item ids which failed to load
        const loadedWorkItemsSet = new Set(loadedWorkItems.map(w => w.id));
        failedWorkItemIds = workItemIdsToLoad.filter(id => !loadedWorkItemsSet.has(id));

        if (failedWorkItemIds.length > 0) {
            yield effects.put(
                WorkItemActions.updateWorkItemsStatus(
                    failedWorkItemIds.map(id => ({
                        workItemId: id,
                        status: WorkItemStatus.LoadingFailed,
                        error: format(Resources.CannotLoadWorkItem, id)
                    }))
                )
            );
        }

        if (loadedWorkItems.length > 0) {
            yield effects.put(WorkItemActions.loadWorkItems(loadedWorkItems));
        }
    }

    yield effects.put(WorkItemActions.workItemsLoadFinished(loadedWorkItems, failedWorkItemIds));
}

function* loadFieldFlags(action: ActionsOfType<WorkItemActions, WorkItemActionTypes.LoadFieldFlags>): SagaIterator {
    const { workItemId } = action.payload;

    const projectGuid: RT<typeof getWorkItemProjectId> = (yield effects.select(getWorkItemProjectId, workItemId)) as any;
    const workItemTypeName: RT<typeof getWorkItemTypeName> = (yield effects.select(getWorkItemTypeName, workItemId)) as any;

    if (!projectGuid || !workItemTypeName) {
        throw new Error("Project id and work item type name are required to load field flags");
    }

    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const fieldFlags: RT<typeof getFieldFlags> = (yield effects.call(getFieldFlags, pageContext, projectGuid, workItemId, workItemTypeName)) as any;

    if (fieldFlags) {
        yield effects.put(WorkItemActions.loadFieldFlagsSucceeded(workItemId, fieldFlags));
    }
}

export function* loadAllowedValues(action: ActionsOfType<WorkItemActions, WorkItemActionTypes.LoadAllowedValues>): SagaIterator {
    const { fieldRefName, workItemId } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const workItemType: RT<typeof getWorkItemTypeName> = (yield effects.select(getWorkItemTypeName, workItemId)) as any;
    const projectGuid: RT<typeof getWorkItemProjectId> = (yield effects.select(getWorkItemProjectId, workItemId)) as any;

    // Get allowed values for a single field
    const allowedValues: RT<typeof WorkItemFieldsAllowedValuesDataSource.getAllowedValuesAsync> = (yield effects.call(
        WorkItemFieldsAllowedValuesDataSource.getAllowedValuesAsync,
        pageContext,
        projectGuid,
        workItemType,
        workItemId,
        fieldRefName
    )) as any;

    // Put an action saying states are loaded
    yield effects.put(WorkItemActions.loadAllowedValuesSucceeded(workItemId, fieldRefName, allowedValues || []));
}

export function* createWorkItem(action: ActionsOfType<WorkItemActions, WorkItemActionTypes.WorkItemCreateRequested>): SagaIterator {
    const { projectId, tempWorkItemId } = action.payload;
    const workItem: RT<typeof getWorkItem> = (yield effects.select(getWorkItem, tempWorkItemId)) as any;
    const canCreate: RT<typeof canCreateWorkItem> = (yield effects.select(canCreateWorkItem, tempWorkItemId)) as any;

    if (workItem && workItem.status !== WorkItemStatus.Saving && isWorkItemNew(workItem.id) && canCreate) {
        // Put an action to set the work item status to saving to block the create button
        yield effects.put(WorkItemActions.updateWorkItemsStatus([{ workItemId: workItem.id, status: WorkItemStatus.Saving }]));
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

        try {
            const createdWorkItemModel: RT<typeof createWorkItemAsync> = (yield effects.call(
                createWorkItemAsync,
                pageContext,
                workItem.projectId,
                workItem.workItemTypeName,
                getFieldsWithValues(workItem),
                workItem.links
            )) as any;
            const { id, fields, relations } = createdWorkItemModel;
            const { links, artifactLinks, attachments } = splitWorkItemRelations(relations);
            const workItemModel: IWorkItem = { projectId, id, fields, links, artifactLinks, attachments };

            yield effects.put(WorkItemActions.removeTemporaryWorkItem(tempWorkItemId));
            yield effects.put(WorkItemActions.loadWorkItems([workItemModel]));
            yield effects.put(WorkItemActions.workItemCreateSucceeded(createdWorkItemModel.id, tempWorkItemId));
        } catch (error) {
            yield effects.put(WorkItemActions.workItemCreateFailed(tempWorkItemId, error.message || error));
            yield effects.call(traceException, pageContext, Area.WIT, Feature.WorkItem, error, {
                action: WorkItemActionTypes.WorkItemCreateFailed
            });
        }
    }
}

export function* deleteWorkItem(action: ActionsOfType<WorkItemActions, WorkItemActionTypes.WorkItemDeleteRequested>): SagaIterator {
    const { projectId, workItemId } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    try {
        const deleteResponse: RT<typeof deleteWorkItemAsync> = (yield effects.call(deleteWorkItemAsync, pageContext, workItemId, projectId)) as any;
        if (deleteResponse.code === 200) {
            yield effects.put(WorkItemActions.workItemDeleteSucceeded(workItemId));
        } else {
            throw new Error(deleteResponse.message);
        }
    } catch (error) {
        yield effects.put(WorkItemActions.setWorkItemError(workItemId, error.message));
        yield effects.put(WorkItemActions.workItemDeleteFailed(workItemId, error.message || error));
        yield effects.call(traceException, pageContext, Area.WIT, Feature.WorkItem, error, {
            action: WorkItemActionTypes.WorkItemDeleteFailed
        });
    }
}

export function* restoreWorkItem(action: ActionsOfType<WorkItemActions, WorkItemActionTypes.WorkItemRestoreRequested>): SagaIterator {
    const { projectId, workItemId } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    try {
        yield effects.call(restoreWorkItemAsync, pageContext, workItemId, projectId);
        yield effects.put(WorkItemActions.workItemRestoreSucceeded(workItemId));
    } catch (error) {
        yield effects.put(WorkItemActions.workItemRestoreFailed(workItemId, error.message || error));
        yield effects.call(traceException, pageContext, Area.WIT, Feature.WorkItem, error, {
            action: WorkItemActionTypes.WorkItemRestoreFailed
        });
    }
}

export function* initializeNewWorkItem(action: ActionsOfType<WorkItemActions, WorkItemActionTypes.InitializeNewWorkItem>): SagaIterator {
    const { tempWorkItemId = getNewTemporaryWorkItemId(), projectId, workItemTypeName, initialValues = {}, initialLinks } = action.payload;

    try {
        const workItemTypeFields: RT<typeof getWorkItemTypeFields> = (yield effects.select(
            getWorkItemTypeFields,
            projectId,
            workItemTypeName
        )) as any;
        if (!workItemTypeFields) {
            yield effects.put(WorkItemActions.newWorkItemInitializationFailed(tempWorkItemId, Resources.CannotInitializeNewWorkItem));
            // We cannot initialize a newWorkItem without typeFields
            throw new Error(Resources.CannotInitializeNewWorkItem);
        }

        const workItem: RT<typeof getWorkItem> = (yield effects.select(getWorkItem, tempWorkItemId)) as any;
        if (workItem) {
            // Temporary workItem already exists. Ensure all the typeFields are initialized correctly
            // For example: A workItem could be have been initialized when typeFields were partially loaded. And the same workItem
            // could be reinitialized once typeFields are loaded completely. So, ensure all the fields are initialized correctly
            const fields = transformWorkItemFields(workItem.fields, workItemTypeFields, /** initializeWithDefaultValues */ true);
            yield effects.put(WorkItemActions.workItemUpdated(workItem.id, fields, { links: initialLinks }));
        } else {
            const newInitialValues: { [refName: string]: FieldValueType } = {};
            for (const key in initialValues) {
                newInitialValues[key.toLocaleLowerCase()] = initialValues[key];
            }
            newInitialValues[CoreFieldRefNames.WorkItemType.toLocaleLowerCase()] = workItemTypeName;

            let areaPathState: IClassificationFieldState | undefined;
            if (!newInitialValues.hasOwnProperty(CoreFieldRefNames.AreaPath.toLocaleLowerCase())) {
                areaPathState = (yield effects.select(getAreaPathState, projectId)) as any;

                if (!areaPathState || !areaPathState.nodes || areaPathState.nodes.length === 0) {
                    yield effects.put(ClassificationNodeActions.getClassificationNodesRequested(projectId, CoreFieldRefNames.AreaPath));
                    yield effects.take(ClassificationNodeActions.getClassificationNodesSucceeded); // This happens after reducer stores area nodes in the state
                }

                const areaNodes: WorkItemClassificationNode[] | undefined = (yield effects.select(
                    getClassificationNodesByType,
                    projectId,
                    CoreFieldRefNames.AreaPath
                )) as any;

                newInitialValues[CoreFieldRefNames.AreaPath.toLocaleLowerCase()] = areaNodes && areaNodes[0].name;
            }

            let iterationPathState: IClassificationFieldState | undefined;
            if (!newInitialValues.hasOwnProperty(CoreFieldRefNames.IterationPath.toLocaleLowerCase())) {
                iterationPathState = (yield effects.select(getIterationPathState, projectId)) as any;

                if (!iterationPathState || !iterationPathState.nodes || iterationPathState.nodes.length === 0) {
                    yield effects.put(ClassificationNodeActions.getClassificationNodesRequested(projectId, CoreFieldRefNames.IterationPath));
                    yield effects.take(ClassificationNodeActions.getClassificationNodesSucceeded); // This happens after reducer stores iteration nodes in the state
                }

                const iterationNodes: RT<typeof getClassificationNodesByType> = (yield effects.select(
                    getClassificationNodesByType,
                    projectId,
                    CoreFieldRefNames.IterationPath
                )) as any;

                newInitialValues[CoreFieldRefNames.IterationPath.toLocaleLowerCase()] = iterationNodes && iterationNodes[0].name;
            }

            // Load temporary work item
            const temporaryWorkItem: IWorkItem = {
                id: tempWorkItemId,
                fields: newInitialValues,
                workItemTypeName,
                projectId,
                links: initialLinks && initialLinks.map(l => l as WorkItemLinkedItem)
            };

            // Initialize the workitem with default values
            temporaryWorkItem.fields = transformWorkItemFields(temporaryWorkItem.fields, workItemTypeFields, true);
            yield effects.put(WorkItemActions.loadWorkItems([temporaryWorkItem]));

            yield effects.put(WorkItemActions.newWorkItemInitialized(tempWorkItemId));

            // Load field flags
            yield effects.put(WorkItemActions.loadFieldFlags(tempWorkItemId));

            // Load mru nodes after load new work item
            if (!areaPathState || !areaPathState.mru || !iterationPathState || !iterationPathState.mru) {
                yield effects.put(ClassificationNodeActions.getClassificationMruRequested(projectId));
            }
        }
    } catch (error) {
        yield effects.put(
            WorkItemActions.updateWorkItemsStatus([
                { workItemId: tempWorkItemId, status: WorkItemStatus.LoadingFailed, error: error.message || error }
            ])
        );
        yield effects.put(WorkItemActions.newWorkItemInitializationFailed(tempWorkItemId, (error && error.message) || ""));
    }
}
